// ---------------------------------------------------------------------------
// V34 — OFFLINE SYNC AND IMPORT.
//
// ★ THE ONE QUESTION THIS SCREEN ANSWERS IS "WHOSE CLOCK DECIDED", and it
//   answers it in the database's own words rather than in a paraphrase.
//
// "Conflict resolution favouring the earliest timestamp" is only meaningful if
// somebody can find out which clock "earliest" was measured on. A device clock
// is settable by its owner, so a queue that simply took the smallest number a
// handset sent would let a slow handset decide when its owner arrived, for ever,
// and nobody would ever see why. The rule lives in `app.clock_ruling.description`
// and this screen renders that column verbatim — no second copy of it exists in
// TypeScript, because the copy is the one that is wrong the day the rule moves.
//
// THE HONEST GAP IS NAMED ON THE SCREEN. Attendance evidence is written once and
// never edited, so a capture that arrives after the fact asserting an earlier
// time cannot rewrite it. The recorded time is the one that pays, this screen
// says so in those words, and the disagreement is shown in minutes so somebody
// can judge whether it matters.
//
// STATUS IS NEVER COLOUR ALONE. Every state carries a word.
//
// NO ARITHMETIC ON A TIME. `minutes` below divides a span of seconds the DATABASE
// computed by sixty to render it; nothing here derives a duration from two
// instants, which is the operation that would make this screen a second opinion
// about when somebody arrived.
// ---------------------------------------------------------------------------

import { Tabs } from "../design/Tabs";
import { useEffect, useState } from "react";

import { send } from "../api/auth";
import {
  imports as importsApi,
  offline as offlineApi,
  type ImportBatch,
  type ImportFormat,
  type ImportOutcome,
  type OfflineConflict,
  type QueueOutcome,
} from "../api/offline";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel, type Translate } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { whenIn } from "../system/format";

type QueueReply = { outcomes?: QueueOutcome[]; error?: { message: string } };
type ConflictsReply = { conflicts?: OfflineConflict[]; error?: { message: string } };
type FormatsReply = { formats?: ImportFormat[]; error?: { message: string } };
type HistoryReply = { batches?: ImportBatch[]; error?: { message: string } };
type UploadReply = {
  import?: {
    batch_id: string;
    lines: number;
    skipped_blank: number;
    outcomes: ImportOutcome[];
    categories: Record<string, Record<string, number>>;
    unknown_columns: string[];
  };
  error?: { message: string };
};

/** A span the database measured, rendered as whole minutes. Negative means the
 *  capture is EARLIER than what stands recorded, which is the case that needs a
 *  person, so the sign is turned into words rather than shown as a minus. */
function minutes(seconds: number | null, t: Translate): string {
  if (seconds === null) return "—";
  const whole = Math.round(Math.abs(seconds) / 60);
  return seconds < 0 ? t("{n} min earlier", { n: whole }) : t("{n} min later", { n: whole });
}

export function OfflineSync() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  // ★ THE ORGANISATION'S CLOCK, NOT THE READER'S. V27a's ruling: a shift happens
  // where the venue is, and this screen's entire subject is which instant is
  // true. It shipped rendering raw ISO strings — `2027-03-01T18:12:04.000Z` in a
  // table a steward reads on a cracked phone at 06:00 — which is not a formatting
  // preference, it is the screen declining to answer its own question. Found by
  // opening the gallery and reading it.
  //
  // NO ARITHMETIC HERE, WHICH IS THE OTHER HALF. `whenIn` formats and computes
  // nothing; every duration on this screen is a number the database sent.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;

  const [queue, setQueue] = useState<QueueOutcome[] | null>(null);
  const [queueFailed, setQueueFailed] = useState<Failure | null>(null);
  const [conflicts, setConflicts] = useState<OfflineConflict[] | null>(null);
  const [conflictsFailed, setConflictsFailed] = useState<Failure | null>(null);
  const [formats, setFormats] = useState<ImportFormat[] | null>(null);
  const [formatsFailed, setFormatsFailed] = useState<Failure | null>(null);
  const [history, setHistory] = useState<ImportBatch[] | null>(null);
  const [historyFailed, setHistoryFailed] = useState<Failure | null>(null);
  const [uploaded, setUploaded] = useState<NonNullable<UploadReply["import"]> | null>(null);
  const [uploadFailed, setUploadFailed] = useState<string | null>(null);
  // "Try again" re-runs every read below; each keeps its status (design/LoadFailed). A reader who may read none of
  // them is told once, in the first panel.
  const [reloads, setReloads] = useState(0);
  const retry = () => setReloads((n) => n + 1);
  const allLimited = [queueFailed, conflictsFailed, formatsFailed, historyFailed].every((f) => f?.status === 403);
  const [chosenFormat, setChosenFormat] = useState<string>("");
  const [reading, setReading] = useState(false);

  // ★ THE AFFORDANCE, NOT ONLY THE ENDPOINT. A working import nobody can reach
  // from a screen is not a feature, and this build has shipped one of those
  // before. The file is read in the browser and sent as text; the delimiter and
  // the column names come from the format the database describes, so a
  // competitor renaming a column is a row change rather than a code change.
  async function readFile(file: File, format: string) {
    setReading(true);
    setUploadFailed(null);
    const text = await file.text();
    const res = await send<UploadReply>(importsApi.upload(apiBase, format, file.name, text));
    setReading(false);
    if (res.status === 200 && res.body?.import) {
      setUploaded(res.body.import);
      const after = await send<HistoryReply>(importsApi.history(apiBase));
      if (after.status === 200 && Array.isArray(after.body?.batches)) setHistory(after.body.batches);
    } else {
      setUploaded(null);
      setUploadFailed(refusal(res.body?.error) ?? t("That file could not be read."));
    }
  }

  useEffect(() => {
    void (async () => {
      const res = await send<QueueReply>(offlineApi.queue(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.outcomes)) {
        setQueue(res.body.outcomes);
        setQueueFailed(null);
      } else {
        setQueue(null);
        setQueueFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<ConflictsReply>(offlineApi.conflicts(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.conflicts)) {
        setConflicts(res.body.conflicts);
        setConflictsFailed(null);
      } else {
        setConflicts(null);
        setConflictsFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<FormatsReply>(importsApi.formats(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.formats)) {
        setFormats(res.body.formats);
        setFormatsFailed(null);
      } else {
        setFormats(null);
        setFormatsFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<HistoryReply>(importsApi.history(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.batches)) {
        setHistory(res.body.batches);
        setHistoryFailed(null);
      } else {
        setHistory(null);
        setHistoryFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, reloads]);

  const waiting = conflicts === null ? [] : conflicts;

  return (
    <div className="stack">
      <h1>{t("Offline sync and import")}</h1>
      <p className="muted">
        {t("Check-ins captured where there was no signal, what became of them, and the people brought in from another system.")}
      </p>

      <Tabs
        label={t("Offline sync and import")}
        tabs={[
          {
            id: "sync-inbox",
            label: t("From handsets"),
            content: (
              <div className="stack">
      {queueFailed !== null ? (
        <section className="panel">
          <LoadFailed failure={queueFailed} fallback={t("The offline queue could not be read.")} onRetry={retry} />
        </section>
      ) : null}

      {queue !== null ? (
        <section className="panel stack">
          <div className="panel-head">
            <h2>{t("What has come in from handsets")}</h2>
          </div>
          {/* Tour T-48 (owner): a summary, then only the outcomes that have something in them. The
              full list stays one click away, so a count of zero still reads as zero. */}
          {queue.some((r) => r.needs_attention && Number(r.captures) > 0) ? (
            <StatusChip tone="attention" label={t("Something here needs a person")} />
          ) : (
            <StatusChip tone="live" label={t("Nothing needs your decision")} />
          )}
          {queue.some((r) => Number(r.captures) > 0) ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Outcome")}</th>
                  <th scope="col">{t("Captures")}</th>
                  <th scope="col">{t("Last seen")}</th>
                </tr>
              </thead>
              <tbody>
                {queue
                  .filter((r) => Number(r.captures) > 0)
                  .map((row) => (
                    <tr key={row.outcome}>
                      <th scope="row">
                        <StatusChip tone={row.needs_attention ? "attention" : "neutral"} label={tl(row.label)} />
                      </th>
                      <td className="numeric">{row.captures}</td>
                      <td>{row.last_received_at === null ? "—" : whenIn(row.last_received_at, orgZone)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">{t("No check-in has come in from a handset yet.")}</p>
          )}
          <details className="role-group">
            <summary>{t("What each outcome means")}</summary>
            <dl className="facts">
              {queue.map((row) => (
                <div key={row.outcome}>
                  <dt>{tl(row.label)}</dt>
                  <dd className="soft">{tl(row.description)}</dd>
                </div>
              ))}
            </dl>
          </details>
        </section>
      ) : null}

      {conflictsFailed !== null ? (
        <section className="panel">
          {allLimited ? null : <LoadFailed failure={conflictsFailed} fallback={t("The conflicts could not be read.")} onRetry={retry} />}
        </section>
      ) : null}

      {conflicts !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Captures somebody has to decide about")}</h2>
            <StatusChip
              tone={waiting.length === 0 ? "live" : "attention"}
              label={waiting.length === 0 ? t("Nothing waiting") : t("{n} waiting", { n: waiting.length })}
            />
          </div>
          {waiting.length === 0 ? (
            <p className="muted">
              {t("Nothing is waiting. Every capture that has reached us was either recorded, or lost cleanly to an earlier one, and neither needs a person.")}
            </p>
          ) : (
            <>
              <p className="muted">
                {t("A check-in already recorded is never edited — it is written once, and what is recorded is what pays. Where a handset disagrees, both times are shown here and the difference is yours to judge.")}
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("Person")}</th>
                    <th scope="col">{t("Event")}</th>
                    <th scope="col">{t("Recorded")}</th>
                    <th scope="col">{t("The handset says")}</th>
                    <th scope="col">{t("Difference")}</th>
                    <th scope="col">{t("Which clock decided")}</th>
                  </tr>
                </thead>
                <tbody>
                  {waiting.map((row) => (
                    <tr key={row.capture_id}>
                      <th scope="row">
                        {row.given_name} {row.family_name}
                      </th>
                      <td>{tl(row.kind_label)}</td>
                      <td>
                        {row.recorded_effective_at === null
                          ? "not recorded"
                          : whenIn(row.recorded_effective_at, orgZone)}
                      </td>
                      <td>{whenIn(row.capture_admitted_at ?? row.device_reported_at, orgZone)}</td>
                      <td>
                        {row.refusal_reason === null ? (
                          minutes(row.disagreement_seconds, t)
                        ) : (
                          <StatusChip tone="failed" label={t("Refused")} />
                        )}
                      </td>
                      <td className="soft">
                        {tl(row.refusal_reason ?? row.ruling_description ?? row.outcome_label)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      ) : null}
              </div>
            ),
          },
          {
            id: "sync-people",
            label: t("Import people"),
            content: (
              <div className="stack">
      {formatsFailed !== null ? (
        <section className="panel">
          {allLimited ? null : <LoadFailed failure={formatsFailed} fallback={t("The import formats could not be read.")} onRetry={retry} />}
        </section>
      ) : null}

      {formats !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Bringing people in from another system")}</h2>
          </div>
          {formats.length === 0 ? (
            <p className="muted">
              {t("No import format is configured. That is a defect rather than an empty state — this product reads at least its own.")}
            </p>
          ) : (
            <ul className="plain-list">
              {formats.map((f) => (
                <li key={f.id}>
                  <strong>{tl(f.label)}</strong>
                  <p className="soft">{tl(f.description)}</p>
                  {f.category_columns.length > 0 ? (
                    <p className="muted">
                      {t("These columns are counted and shown to you ({columns}), and never stored: in Verbunk a role belongs to a shift, not to a person.", {
                        columns: f.category_columns.join(", "),
                      })}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {formats !== null && formats.length > 0 ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Read a file")}</h2>
            {reading ? <StatusChip tone="attention" label={t("Reading")} /> : null}
          </div>
          <div className="field">
            <label htmlFor="v34-format">{t("Which system it came from")}</label>
            <select
              id="v34-format"
              value={chosenFormat}
              onChange={(e) => setChosenFormat(e.target.value)}
            >
              <option value="">{t("Choose a format")}</option>
              {formats.map((f) => (
                <option key={f.id} value={f.id}>
                  {tl(f.label)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="v34-file">{t("The file")}</label>
            <input
              id="v34-file"
              type="file"
              accept=".csv,text/csv,text/plain"
              disabled={chosenFormat === ""}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && chosenFormat !== "") void readFile(file, chosenFormat);
              }}
            />
            <p className="field-hint">
              {t("Choose the format first. Read with the wrong one, every line comes back with nothing in it rather than creating people with no names.")}
            </p>
          </div>
        </section>
      ) : null}

      {uploadFailed !== null ? (
        <section className="panel">
          <StatusChip tone="failed" label={t("That file was not read")} />
          <p className="muted">{uploadFailed}</p>
        </section>
      ) : null}

      {uploaded !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("The last file read")}</h2>
            <StatusChip tone="live" label={t("{n} lines", { n: uploaded.lines })} />
          </div>
          <p className="muted">
            {t("{n} lines, {blank} blank.", { n: uploaded.lines, blank: uploaded.skipped_blank })}
          </p>
          {Object.keys(uploaded.categories).length > 0 ? (
            <>
              <h3>{t("What its category columns contained")}</h3>
              <p className="muted">
                {t("None of this has been stored. In Verbunk a role belongs to a shift and not to a person, so there is nowhere for these to go — they are shown so you know what was in the file rather than wondering where it went.")}
              </p>
              <ul className="plain-list">
                {Object.entries(uploaded.categories).map(([column, counts]) => (
                  <li key={column}>
                    <strong>{column}</strong>
                    <p className="soft">
                      {Object.entries(counts)
                        .map(([label, n]) => `${label} (${String(n)})`)
                        .join(", ")}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {uploaded.unknown_columns.length > 0 ? (
            <p className="muted">
              {t("Columns this format does not name, and which were ignored: {columns}. If that list is most of the file, the format is the wrong one.", {
                columns: uploaded.unknown_columns.join(", "),
              })}
            </p>
          ) : null}
        </section>
      ) : null}

      {historyFailed !== null ? (
        <section className="panel">
          {allLimited ? null : <LoadFailed failure={historyFailed} fallback={t("The import history could not be read.")} onRetry={retry} />}
        </section>
      ) : null}

      {history !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Files read before")}</h2>
          </div>
          {history.length === 0 ? (
            <p className="muted">
              {t("Nothing has been imported yet. A first import is a file exported from whatever you use today — nobody is asked to retype anybody.")}
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("File")}</th>
                  <th scope="col">{t("Format")}</th>
                  <th scope="col">{t("Added")}</th>
                  <th scope="col">{t("Already here")}</th>
                  <th scope="col">{t("Not enough to add")}</th>
                  <th scope="col">{t("Refused")}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.batch_id}>
                    <th scope="row">{b.filename}</th>
                    <td>{tl(b.format_label)}</td>
                    <td className="numeric">{b.created}</td>
                    <td className="numeric">{b.matched}</td>
                    <td className="numeric">{b.incomplete}</td>
                    <td className="numeric">{b.refused}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
